import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react';
import { EnterOutlined } from '@ant-design/icons';
import styles from './FileManagerBreadcrumb.module.scss';

// Custom rather than antd's Breadcrumb: this one needs a fixed back button and a
// scrollable overflow with edge fades, neither of which antd's offers.

export interface BreadcrumbItem {
  id: string;
  label: string;
}

interface FileManagerBreadcrumbProps {
  /** Root first, current folder last. */
  items: BreadcrumbItem[];
  onNavigate: (id: string) => void;
  className?: string;
}

const FileManagerBreadcrumb = ({ items, onNavigate, className }: FileManagerBreadcrumbProps) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollStart, setCanScrollStart] = useState(false);
  const [canScrollEnd, setCanScrollEnd] = useState(false);

  const pathKey = items.map((item) => item.id).join('/');
  const parent = items.length > 1 ? items[items.length - 2] : null;

  const updateEdges = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    setCanScrollStart(scroller.scrollLeft > 0);
    setCanScrollEnd(scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth - 1);
  }, []);

  // Keep the current folder in view whenever the path changes.
  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    if (scroller) scroller.scrollLeft = scroller.scrollWidth;
    updateEdges();
  }, [pathKey, updateEdges]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const observer = new ResizeObserver(updateEdges);
    observer.observe(scroller);
    return () => observer.disconnect();
  }, [updateEdges]);

  const handleBack = () => {
    if (parent) onNavigate(parent.id);
  };
  const handleCrumbClick = (event: MouseEvent<HTMLButtonElement>) => onNavigate(event.currentTarget.dataset.id ?? '');

  const fadeClass = [canScrollStart && styles.fadeStart, canScrollEnd && styles.fadeEnd].filter(Boolean).join(' ');

  return (
    <nav aria-label="Breadcrumb" className={`${styles.bar} ${className ?? ''}`}>
      <button type="button" className={styles.back} aria-label="Back one level" disabled={!parent} onClick={handleBack}>
        <EnterOutlined />
      </button>
      <div ref={scrollerRef} className={`${styles.scroller} ${fadeClass}`} onScroll={updateEdges}>
        <ol className={styles.list}>
          {items.map((item, index) => (
            <li key={item.id} className={styles.item}>
              {index === items.length - 1 ? (
                <span className={`${styles.crumb} ${styles.current}`} aria-current="page">
                  {item.label}
                </span>
              ) : (
                <button type="button" className={styles.crumb} data-id={item.id} onClick={handleCrumbClick}>
                  {item.label}
                </button>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};

export default memo(FileManagerBreadcrumb);
